import React, { useEffect, useState } from 'react';
import {
  Card,
  CardHeader,
  CardBody,
  Container,
  Row,
  Col,
  Table,
  Spinner,
  Button
} from 'reactstrap';
import { Link } from 'react-router-dom';
import { Check, CloudDownload, InfoCircle, X } from 'react-bootstrap-icons';
import { useTranslation } from 'react-i18next';
import SimpleHeader from '../../components/Headers/SimpleHeader';
import client from '../../utils/client';
import SweetAlerts from '../../components/SweetAlerts';

const Invoices = () => {
  const { t } = useTranslation();
  const [inProgress, setInProgress] = useState(null);
  const [invoices, setInvoices] = useState(null);
  const [invoiceData, setInvoiceData] = useState(null);

  useEffect(() => {
    document.body.classList.add('visits');
    return () => document.body.classList.remove('visits');
  }, []);

  useEffect(() => {
    (async () => {
      setInProgress(true);
      let { data } = await client('admin-gc/invoices');

      if (data) {
        data = data.map((i) => {
          if (i.canceled) {
            i.other = t('invoices.canceled', 'Makulerad');
          } else if (i.credited) {
            i.other = t('invoices.credited', 'Krediterad');
          } else if (i.creditFor) {
            i.other = t('invoices.creditFor', { invoiceId: i.creditFor });
          }
          return i;
        });

        setInvoices(data);
      }

      setInProgress(null);
    })();
  }, []);

  const fetchInvoiceInformation = async (invoiceId) => {
    const { data } = await client(`admin-gc/invoices/${invoiceId}?from-fortnox=1`);

    setInvoiceData(data);
  };

  return (
    <>
      <SimpleHeader name={t('invoices.title', 'Fakturor')} />

      <Container className="mt--6" fluid>
        <Row>
          <Col xs="12" md="9">
            <Row>
              <Col>
                <Card>
                  <CardHeader>
                    <h3 className="mb-0">{t('invoices.title')}</h3>
                  </CardHeader>
                  <CardBody>
                    {inProgress && <Spinner />}
                    <Table responsive>
                      <thead className="thead-light">
                        <tr>
                          <th>{t('default.date')}</th>
                          <th>{t('invoices.invoice', 'Faktura')}</th>
                          <th>{t('default.product', 'Produkt')}</th>
                          <th>{t('invoices.sum-ex-vat', 'Summa ex. moms')}</th>
                          <th>{t('invoices.other', 'Övrigt')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {invoices?.map((i) => (
                          <tr key={i.id}>
                            <td>{i.date}</td>
                            <td>
                              {i.id}{' '}
                              <Button
                                color="link"
                                onClick={() => fetchInvoiceInformation(i.id)}
                                className="p-0 mt--1"
                              >
                                <InfoCircle />
                              </Button>
                              <Link
                                to={`/golfclub-admin/invoice?invoiceId=${i.id}`}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                <CloudDownload size={20} />
                              </Link>
                            </td>
                            <td>{i.products}</td>
                            <td>{i.sumExclVat}</td>
                            <td>{i.other}</td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </CardBody>
                </Card>
              </Col>
            </Row>
          </Col>
        </Row>
      </Container>

      {invoiceData && (
        <SweetAlerts type="info" title="Fakturainformation" onConfirm={() => setInvoiceData(null)}>
          <Table className="align-items-center mt-2" responsive>
            <tbody>
              <tr>
                <th>{t('invoices.invoice')}</th>
                <td>{invoiceData.invoice.id}</td>
              </tr>
              <tr>
                <th>{t('invoices.invoice-date', 'Fakturadatum')}</th>
                <td>{invoiceData.fortnox.date}</td>
              </tr>
              <tr>
                <th>{t('invoices.due-date', 'Förfallodatum')}</th>
                <td>{invoiceData.fortnox.dueDate}</td>
              </tr>
              <tr>
                <th>{t('invoices.sum-ex-vat')}</th>
                <td>{invoiceData.invoice.sumExclVat}</td>
              </tr>
              <tr>
                <th>{t('invoices.paid', 'Betald')}</th>
                <td>
                  {invoiceData.fortnox.paid ? (
                    <>
                      <Check size={22} color="#2dce89" className="mr-2" />
                      {invoiceData.fortnox.paymentDate}
                    </>
                  ) : (
                    <X size={22} color="#f5365c" />
                  )}
                </td>
              </tr>
            </tbody>
          </Table>
        </SweetAlerts>
      )}
    </>
  );
};

export default Invoices;
